import { TypeFormFieldsWithoutUnresolvableLinksResponse } from '@/features/contentful/_generated';
import classNames from 'classnames';
import React from 'react';
import { countries } from 'countries-list';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';

interface IFormFieldProps {
    formField: TypeFormFieldsWithoutUnresolvableLinksResponse;
    id?: string;
    error?: string;
    validateOnChangeEvent?: (e: any) => void;
    pleaseSelectLabel: string;
}

const commonClass =
    'tw-appearance-none tw-border tw-rounded tw-w-full tw-py-3 tw-px-3 tw-text-gray-700 tw-leading-normal tw-focus:outline-none tw-focus:shadow-outline tw-block tw-bg-white tw-bg-clip-padding tw-transition tw-duration-150 tw-ease-in-out tw-border-gray-400 tw-font-gotham-book';
const labelClasses =
    'tw-text-[#4b4f54] tw-text-[14px] tw-font-gotham-book tw-leading-[22px] tw-mb-1';
const errorInputClass = 'tw-border-red-500';
const errorInfoClass = 'tw-text-red-500 tw-text-[13px] tw-font-gotham-bold tw-mt-1';

const InputField: React.FC<{
    type: string;
    id: string;
    name: string;
    value?: string;
    required: boolean;
    onChange?: (e: any) => void;
    error?: string;
    label?: string;
    formId?: string;
}> = ({ type, id, name, value, required, onChange, error, label, formId }) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: formId });
    return (
        <div
            className={classNames('tw-mb-5', {
                'tw-w-full': name !== 'First name' && name !== 'Last name',
                'tw-w-[47%]': name === 'First name' || name === 'Last name',
                'tw-float-left': name === 'First name',
                'tw-float-right': name === 'Last name',
            })}
        >
            <label
                className={labelClasses}
                htmlFor={id}
                {...inspectorProps({
                    fieldId: 'label',
                })}
            >
                {label}
                {required && <span>*</span>}
            </label>
            <input
                className={classNames(commonClass, { [errorInputClass]: error })}
                type={type}
                id={id}
                name={name}
                value={value}
                required={required}
                onChange={onChange}
            />
            {error && <span className={errorInfoClass}>{error}</span>}
        </div>
    );
};

const SelectField: React.FC<{
    id: string;
    name: string;
    required: boolean;
    onChange?: (e: any) => void;
    error?: string;
    label?: string;
    options: { key: string; value: string }[];
    formId?: string;
    value?: string;
    pleaseSelectLabel: string;
}> = ({
    id,
    name,
    required,
    onChange,
    error,
    label,
    options,
    formId,
    value,
    pleaseSelectLabel,
}) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: formId });
    return (
        <div className="tw-mb-5">
            <label
                className={labelClasses}
                htmlFor={id}
                {...inspectorProps({
                    fieldId: 'label',
                })}
            >
                {label}
                {required && <span>*</span>}
            </label>
            <select
                className={classNames(commonClass, 'tw-bg-no-repeat tw-bg-contain', {
                    [errorInputClass]: error,
                })}
                id={id}
                style={{
                    backgroundImage: `url("/images/select-arrow.svg")`,
                    backgroundPosition: 'right .7em top 50%, 0 0',
                    backgroundSize: '.65em auto, 100%',
                }}
                required={required}
                onChange={onChange}
                defaultValue={value ?? ''}
                name={name}
            >
                <option value="" disabled>
                    {pleaseSelectLabel}
                </option>
                {options.map((option) => (
                    <option key={option.key} value={option.value}>
                        {option.key}
                    </option>
                ))}
            </select>
            {error && <span className={errorInfoClass}>{error}</span>}
        </div>
    );
};

const FormFieldItem: React.FC<IFormFieldProps> = ({
    formField,
    error,
    validateOnChangeEvent,
    id,
    pleaseSelectLabel,
}) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    const { type, cssId, value, fieldName, required, fieldLabel, selectDropdown } =
        formField?.fields || {};
    switch (formField?.fields?.type) {
        case 'hidden': {
            return <input type="hidden" id={cssId} name={fieldName} value={value} />;
        }
        case 'email':
        case 'text':
        case 'number':
            return (
                <InputField
                    type={type}
                    id={cssId}
                    name={fieldName}
                    value={value}
                    required={required === 'Yes'}
                    onChange={validateOnChangeEvent}
                    error={error}
                    label={fieldLabel}
                    formId={id}
                />
            );
        case 'textarea':
            return (
                <div className="tw-mb-5">
                    <label
                        className={labelClasses}
                        htmlFor={cssId}
                        {...inspectorProps({
                            fieldId: 'label',
                        })}
                    >
                        {fieldLabel}
                        {required === 'Yes' && <span>*</span>}
                    </label>
                    <textarea
                        className={classNames(
                            'tw-w-full tw-py-2 tw-px-3 tw-text-[#4b4f54] tw-text-[14px] tw-font-gotham-book tw-leading-[22px] tw-border tw-border-[#4b4f54] tw-rounded-lg tw-min-h-36 tw-mb-2',
                            { [errorInputClass]: error },
                        )}
                        id={cssId}
                        name={fieldName}
                        required={required === 'Yes'}
                        onChange={validateOnChangeEvent}
                    />
                    {error && <span className={errorInfoClass}>{error}</span>}
                </div>
            );
        case 'select':
            return (
                <SelectField
                    id={cssId}
                    name={fieldName}
                    required={required === 'Yes'}
                    onChange={validateOnChangeEvent}
                    error={error}
                    label={fieldLabel}
                    options={selectDropdown as any[]}
                    formId={id}
                    value={value}
                    pleaseSelectLabel={pleaseSelectLabel}
                />
            );
        case 'select-country':
            return (
                <SelectField
                    id={cssId}
                    name={fieldName}
                    required={required === 'Yes'}
                    onChange={validateOnChangeEvent}
                    error={error}
                    label={fieldLabel}
                    options={Object.values(countries).map((country) => ({
                        key: country.name,
                        value: country.name,
                    }))}
                    formId={id}
                    pleaseSelectLabel={pleaseSelectLabel}
                />
            );
        default:
            return null;
    }
};

export default FormFieldItem;
